import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Row } from 'antd';
import styles from './RecipeList.module.css';
import RecipeCard from '@components/RecipeCard/RecipeCard';
import { RecipeResponse } from '@src/types/core_t';
import useQueryString from '@common/hooks/useQueryString';
import 'react-perfect-scrollbar/dist/css/styles.css';
import StyledPagination from '@components/StyledPagination/StyledPagination';
import { SearchInputContextType } from '@src/contexts/SearchInputContext';
import useTranslation from '@hooks/useTranslation';
import RenameModal from '@components/RenameModal/RenameModal';
import recipeApi, { RecipeSearchResult } from '@src/api/recipe/recipeApi';
import useKemuNotification from '@hooks/useKemuNotification';
import CardSkeleton from '@components/marketplace/CardSkeleton/CardSkeleton';

const resultsPerPage = 11;

const GridResults = (props: {
	hits: RecipeResponse[];
	hitRenderer: (hit: RecipeResponse) => React.JSX.Element;
}) => {
	const { hits, hitRenderer } = props;

	return (
    <Row gutter={{ xs: 0, sm: 12, md: 16, lg: 16, xl: 24 }} className={styles.KemuRow}>
      {hits.map((hit) => (
        <Col key={hit.id} xs={24} sm={12} md={12} lg={8} xl={6} xxl={6}>
          {hitRenderer(hit)}
        </Col>
      ))}
    </Row>
  );
};

type Props = {
	onSetSearchContext: (context: SearchInputContextType) => void;
};

const RecipeList = ({ onSetSearchContext }: Props): React.JSX.Element | null => {
	const queryString = useQueryString();
	const selectedCard = queryString.get('select');
	const kemuNotification = useKemuNotification();

	const [renamingRecipeDetails, setRenamingRecipeDetails] = useState<RecipeResponse | null>(null);
	const [results, setResults] = useState<RecipeSearchResult | null>(null);
	const [loading, setLoading] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [currentQuery, setCurrentQuery] = useState('');
	const t = useTranslation('Marketplace.MyRecipes');
	const requestIdRef = useRef(0);

	const handleGlobalSearch = useCallback(async (query: string) => {
		const requestId = ++requestIdRef.current;
		setLoading(true);
		try {
			const result = await recipeApi.searchRecipes({ query, page: 1, perPage: resultsPerPage });
			if (requestId !== requestIdRef.current) return;
			setResults(result);
			setCurrentPage(1);
			setCurrentQuery(query);
		} catch (e) {
			if (requestId !== requestIdRef.current) return;
			kemuNotification({ code: 'recipe-search', type: 'error', message: e.message || t('SearchError', 'Search failed') });
		} finally {
			if (requestId === requestIdRef.current) setLoading(false);
		}
	}, [kemuNotification, t]);

	const handlePageChange = useCallback(async (page: number) => {
		const requestId = ++requestIdRef.current;
		setLoading(true);
		try {
			const result = await recipeApi.searchRecipes({ query: currentQuery, page, perPage: resultsPerPage });
			if (requestId !== requestIdRef.current) return;
			setResults(result);
			setCurrentPage(page);
		} catch (e) {
			if (requestId !== requestIdRef.current) return;
			kemuNotification({ code: 'recipe-search', type: 'error', message: e.message || t('PageError', 'Failed to load page') });
		} finally {
			if (requestId === requestIdRef.current) setLoading(false);
		}
	}, [currentQuery, kemuNotification, t]);

	// TODO: Move all these operations to redux reducers
	/** Rename a recipe */
	const handleRenaming = async (name: string) => {
		if (!renamingRecipeDetails) {
			return;
		}

		setRenamingRecipeDetails(null);
		try {
			kemuNotification({
				code: 'recipe-update',
				type: 'info',
				message: t('Card.Menu.Rename.Progress'),
				showProgress: true,
			});

			const updatedDetails = await recipeApi.patchRecipe(renamingRecipeDetails?.id, { name });
			setResults(results => {
				return {
					...results,
					documents: results?.documents?.map(doc => doc.id === renamingRecipeDetails?.id ? updatedDetails : doc) || []
				} as RecipeSearchResult;
			});

			kemuNotification({
				code: 'recipe-update',
				message: t('Card.Menu.Rename.Success'),
				type: 'success',
			});

			// onForceReload && onForceReload();
		} catch (e) {
			kemuNotification({
				code: 'recipe-update',
				title: t('Card.Menu.Rename.Error', 'Rename Failed'),
				message: e.message || 'Unknown error',
				type: 'error',
			});
		}
	};

	/** Handle recipe deletion by immediately removing it from local search results */
	const handleRecipeDeleted = useCallback((recipeId: string) => {
		setResults(results => {
			if (!results) {
				return results;
			}
			return {
				...results,
				documents: results.documents.filter(doc => doc.id !== recipeId),
				total: Math.max(0, results.total - 1)
			};
		});
	}, []);

	// Register the search handler when the component mounts
	useEffect(() => {
		onSetSearchContext?.({
			onSearch: handleGlobalSearch,
		});
	}, [onSetSearchContext, handleGlobalSearch]);

	return (
		<div className={styles.RecipeList}>
			{/* {contextHolder} */}
			<RenameModal
				onSubmit={handleRenaming}
				onClose={() => setRenamingRecipeDetails(null)}
				currentName={renamingRecipeDetails?.name || ''}
				open={!!renamingRecipeDetails}
			/>

			{/* <RecipeShare onDetailsUpdated={onUserListUpdated} recipeId={cardDetails.id} shareList={sharedWithList} publicShareId={publicUrl} visible={shareDialogVisible} onClose={closeShareDialog}/> */}

			{!!results?.documents.length && (
				<StyledPagination
					wrapperClassName={styles.PaginationWrapper}
					current={results.page}
					total={results.total}
					onChange={handlePageChange}
					pageSize={resultsPerPage}
					showSizeChanger={false}
				/>
			)}

			{loading ? (
				<CardSkeleton totalCards={resultsPerPage} />
			) : (
				<>
					<GridResults
						hits={results?.documents || []}
						hitRenderer={
							(hit: RecipeResponse) => (
								<RecipeCard
									recipe={hit}
									selected={selectedCard === hit.id}
									onRename={setRenamingRecipeDetails}
									onRecipeDeleted={handleRecipeDeleted}
								/>
							)
						}
					/>

					{results?.documents && results.documents.length === 0 && (
						<div className={styles.NoResults}>
							<p>{t('NoneFound')}</p>
						</div>
					)}
				</>
			)}


		</div>
  );
};


export default RecipeList;
